import React from 'react';

import PropTypes from 'prop-types';

import { StylableComponent, stylePropType } from '@adsk/alloy-react-helpers';
import { ArrowRightIcon, CalendarFilledIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';

import FieldValue from './FieldValue';

type FieldProps = StylableComponent<HTMLButtonElement> & {
  open?: boolean;
  value?:
    | string
    | null
    | [string | null | undefined, string | null | undefined];
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  disabled?: boolean;
  error?: boolean;
  range?: boolean;
};

const Field = React.forwardRef<HTMLButtonElement, FieldProps>(
  (
    {
      className,
      style,
      open,
      value,
      placeholder,
      startDatePlaceholder,
      endDatePlaceholder,
      disabled,
      error,
      range,
      ...props
    },
    ref
  ) => {
    const [startDate, endDate] = Array.isArray(value) ? value : [value];

    const rangeDatesSelected = range ? startDate && endDate : false;

    const fieldValueBag = {
      disabled,
      style: { margin: '0 10px' },
    };

    return (
      <button
        type="button"
        disabled={disabled}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            height: 36,
            width: 300,
            cursor: disabled ? 'default' : 'pointer',
            padding: '0 14px',
            alignItems: 'center',
            outline: 'none',
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.charcoal400}`,
            borderRadius: 2,
            transition: theme.transitions.create([
              'box-shadow',
              'border-color',
            ]),

            '&:hover': {
              boxShadow: theme.halos.hover,
            },

            '&:focus': {
              boxShadow: theme.halos.focus,
              borderColor: theme.colors.adskBlue500,
            },

            '&:active': {
              boxShadow: theme.halos.active,
              borderColor: theme.colors.charcoal400,
            },

            '& svg': {
              color: disabled
                ? theme.colors.charcoal500
                : theme.colors.charcoal700,
            },

            ...(disabled && {
              backgroundColor: theme.colors.charcoal050,
              borderColor: theme.colors.charcoal200,
              '&:hover, &:focus, &:active': {
                borderColor: theme.colors.charcoal200,
                boxShadow: 'none',
              },
            }),

            ...(error && {
              borderColor: theme.colors.red500,
              '&:hover, &:focus, &:active': {
                borderColor: theme.colors.red500,
              },
            }),
          },
          style,
        ]}
        {...props}
      >
        <CalendarFilledIcon size={16} />
        <FieldValue
          value={startDate}
          selecting={open && !rangeDatesSelected}
          placeholder={range ? startDatePlaceholder : placeholder}
          data-testid="DatePickerFieldValue-start"
          {...fieldValueBag}
        />
        {range && (
          <>
            <ArrowRightIcon />
            <FieldValue
              value={endDate}
              selecting={open && !rangeDatesSelected}
              placeholder={endDatePlaceholder}
              data-testid="DatePickerFieldValue-end"
              {...fieldValueBag}
            />
          </>
        )}
      </button>
    );
  }
);

Field.displayName = 'Field';

Field.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The value to display, should be the formatted date(s) string(s) */
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
  /** Placeholder to display when there is no value */
  placeholder: PropTypes.string,
  /** Passed to the field, startDatePlaceholder to display when there is no value */
  startDatePlaceholder: PropTypes.string,
  /** Passed to the field, endDatePlaceHolder to display when there is no value */
  endDatePlaceholder: PropTypes.string,
  /** Indicates whether the overlay is open */
  open: PropTypes.bool,
  /** Whether we should display range state */
  range: PropTypes.bool,
  /** Whether the field is disabled */
  disabled: PropTypes.bool,
  /** Whether the field should display error state */
  error: PropTypes.bool,
};

export default Field;
